import { ChangeEvent, ComponentType, useRef } from 'react';
import { Controller } from 'react-hook-form';

import { ReactComponent as LogoBlack } from 'assets/icons/logo-horizontal-black.svg';
import { Textarea as TextareaBase } from 'components/Form/subcomponents/Textarea';
import { URLS } from 'constants/urls';

import type { FieldId, SectionKey, OptionsState, PageLabels, FieldConfigMap } from '../../types';

import {
  ActionButton,
  CheckRow,
  ErrorText,
  Field,
  FieldLabel,
  FieldSubtitle,
  Footer,
  FormCard,
  HiddenFileInput,
  LockedFieldText,
  LogoImage,
  LogoWrapper,
  Page,
  PageTitle,
  PhotoActions,
  PhotoButton,
  PhotoThumb,
  PhotoThumbRemove,
  PhotoThumbs,
  RequiredMark,
  SectionTitle,
  StyledSelect,
  StyledInput,
  StyledMaskedInput,
  SuccessCard,
  SupportText,
  SuccessContainer,
  SuccessIconWrapper,
  SuccessText,
  SuccessTitle,
  TermsCheckbox,
  TermsLink,
  TermsRow,
  TermsText,
  TrackingTitle,
} from './styled';

const MAX_PHOTOS = 3;
const ALLOWED_EXTENSIONS = ['jpeg', 'jpg', 'png', 'bmp', 'qoi', 'gif', 'tga', 'pbm', 'webp', 'tiff', 'tif'];
const REACT_APP_URL_IMAGES = process.env.REACT_APP_URL_IMAGES || '';

const Textarea = TextareaBase as ComponentType<any>;
const Input = StyledInput as ComponentType<any>;
const MaskedInput = StyledMaskedInput as ComponentType<any>;

interface ChamadoSemLoginProps {
  revealedSections: SectionKey[];
  isSuccess: boolean;
  fieldConfig: FieldConfigMap;
  options: OptionsState;
  pageLabels: PageLabels;
  isSubmitting: boolean;
  notifyRequestersByEmail: boolean;
  unitLabel: string;
  lockedLabelOf: (id: FieldId) => string;
  continueSection: (current: SectionKey) => void;
  handleSubmit: () => void;
  control: any;
  errors: any;
  watch: any;
  setValue: any;
  isVisible: (id: FieldId) => boolean;
  isRequired: (id: FieldId) => boolean;
  isLocked: (id: FieldId) => boolean;
  labelOf: (id: FieldId) => string;
  clearDependents: (id: 'unit' | 'areaGroup' | 'areaSubgroup' | 'area' | 'serviceType') => void;
  searchArea: (term: string) => void;
  loadMoreAreas: () => void;
  searchEquipamento: (term: string) => void;
  loadMoreEquipamentos: () => void;
}

// ── Icons ────────────────────────────────────────────────────────────────────

const ContactIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      d="M11.9998 11.9996C14.2723 11.9996 16.1141 10.1195 16.1141 7.79961C16.1141 5.47977 14.2723 3.59961 11.9998 3.59961C9.7273 3.59961 7.88552 5.47977 7.88552 7.79961C7.88552 10.1195 9.7273 11.9996 11.9998 11.9996ZM14.8798 13.0496H14.343C13.6294 13.3843 12.8355 13.5746 11.9998 13.5746C11.1641 13.5746 10.3734 13.3843 9.65659 13.0496H9.1198C6.7348 13.0496 4.7998 15.0249 4.7998 17.4596V18.8246C4.7998 19.6941 5.49088 20.3996 6.34266 20.3996H17.6569C18.5087 20.3996 19.1998 19.6941 19.1998 18.8246V17.4596C19.1998 15.0249 17.2648 13.0496 14.8798 13.0496Z"
      fill="#62574F"
    />
  </svg>
);

const LocationIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      d="M5.40334 9.75249C5.40334 6.35334 8.35882 3.6001 12.0016 3.6001C15.6444 3.6001 18.5999 6.35334 18.5999 9.75249C18.5999 13.6442 14.4691 18.3091 12.7439 20.0869C12.3384 20.5045 11.6614 20.5045 11.2559 20.0869C9.5307 18.3091 5.3999 13.6442 5.3999 9.75249H5.40334ZM12.0018 11.9442C13.3252 11.9442 14.4012 10.8742 14.4012 9.55816C14.4012 8.24212 13.3252 7.17214 12.0018 7.17214C10.6784 7.17214 9.6024 8.24212 9.6024 9.55816C9.6024 10.8742 10.6784 11.9442 12.0018 11.9442Z"
      fill="#62574F"
    />
  </svg>
);

const SituationIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      d="M12 3.6001C13.3061 3.6001 14.4187 4.47598 14.8281 5.69971H16C17.1031 5.69971 18 6.64201 18 7.80029V18.3003C17.9999 19.4585 17.1031 20.3999 16 20.3999H8C6.89694 20.3999 6.0001 19.4585 6 18.3003V7.80029C6 6.64201 6.89688 5.69971 8 5.69971H9.17188C9.58135 4.47598 10.6939 3.6001 12 3.6001ZM9 15.938C8.58438 15.938 8.25 16.2887 8.25 16.7251C8.25 17.1615 8.58437 17.5122 9 17.5122C9.41563 17.5122 9.75 17.1615 9.75 16.7251C9.75 16.2887 9.41562 15.938 9 15.938ZM11.25 16.1997C11.1126 16.1997 11.0001 16.3181 11 16.4624V16.9878C11.0001 17.1321 11.1126 17.2505 11.25 17.2505H15.75C15.8874 17.2505 15.9999 17.1321 16 16.9878V16.4624C15.9999 16.3181 15.8874 16.1997 15.75 16.1997H11.25ZM9 12.7876C8.5845 12.7876 8.2502 13.1385 8.25 13.5747C8.25 14.0111 8.58437 14.3628 9 14.3628C9.41563 14.3628 9.75 14.0111 9.75 13.5747C9.7498 13.1385 9.4155 12.7876 9 12.7876ZM11.25 13.0503C11.1125 13.0503 11 13.1686 11 13.313V13.8374C11.1125 13.9818 11.25 14.1001 11.25 14.1001H15.75C15.8875 14.1001 16 13.9818 16 13.8374V13.313C16 13.1686 15.8875 13.0503 15.75 13.0503H11.25ZM9 9.63721C8.58437 9.63721 8.25 9.98889 8.25 10.4253C8.2501 10.8616 8.58444 11.2124 9 11.2124C9.41556 11.2124 9.7499 10.8616 9.75 10.4253C9.75 9.98889 9.41563 9.63721 9 9.63721ZM11.25 9.8999C11.1125 9.8999 11 10.0182 11 10.1626V10.688C11.0002 10.8322 11.1126 10.9497 11.25 10.9497H15.75C15.8874 10.9497 15.9998 10.8322 16 10.688V10.1626C16 10.0182 15.8875 9.8999 15.75 9.8999H11.25ZM12 5.69971C11.7349 5.69971 11.4805 5.81054 11.293 6.00732C11.1054 6.20424 11 6.47201 11 6.75049C11.0001 7.02882 11.1055 7.29585 11.293 7.49268C11.4805 7.68953 11.7348 7.80029 12 7.80029C12.2652 7.80029 12.5195 7.68953 12.707 7.49268C12.8945 7.29585 12.9999 7.02882 13 6.75049C13 6.47201 12.8946 6.20424 12.707 6.00732C12.5195 5.81054 12.2651 5.69971 12 5.69971Z"
      fill="#62574F"
    />
  </svg>
);

const GalleryIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      d="M6.88889 4C6.0309 4 5.33333 4.76875 5.33333 5.71429V11.7143C5.33333 12.6598 6.0309 13.4286 6.88889 13.4286H15.4444C16.3024 13.4286 17 12.6598 17 11.7143V5.71429C17 4.76875 16.3024 4 15.4444 4H6.88889ZM12.625 6.85804L14.9583 10.7152C15.0774 10.9134 15.0896 11.1652 14.9875 11.3741C14.8854 11.583 14.6885 11.7143 14.4722 11.7143H10.9722H9.80556H7.86111C7.6375 11.7143 7.43333 11.5723 7.33611 11.35C7.23889 11.1277 7.26562 10.8625 7.4066 10.6696L8.96215 8.52679C9.07396 8.37411 9.23924 8.28571 9.41667 8.28571C9.5941 8.28571 9.76181 8.37411 9.87118 8.52679L10.2917 9.10536L11.6528 6.85536C11.7622 6.67857 11.9444 6.57143 12.1389 6.57143C12.3333 6.57143 12.5156 6.67857 12.625 6.85804ZM7.66667 6.57143C7.66667 6.3441 7.74861 6.12608 7.89447 5.96534C8.04033 5.80459 8.23816 5.71429 8.44444 5.71429C8.65072 5.71429 8.84855 5.80459 8.99442 5.96534C9.14028 6.12608 9.22222 6.3441 9.22222 6.57143C9.22222 6.79876 9.14028 7.01677 8.99442 7.17752C8.84855 7.33827 8.65072 7.42857 8.44444 7.42857C8.23816 7.42857 8.04033 7.33827 7.89447 7.17752C7.74861 7.01677 7.66667 6.79876 7.66667 6.57143ZM4.16667 6.35714C4.16667 6.00089 3.9066 5.71429 3.58333 5.71429C3.26007 5.71429 3 6.00089 3 6.35714V12.3571C3 14.3687 4.48021 16 6.30556 16H14.0833C14.4066 16 14.6667 15.7134 14.6667 15.3571C14.6667 15.0009 14.4066 14.7143 14.0833 14.7143H6.30556C5.12431 14.7143 4.16667 13.6589 4.16667 12.3571V6.35714Z"
      fill="#62574F"
    />
  </svg>
);

const WhatsAppIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.002 3C13.8615 3.00011 17 6.14044 17 10C17 13.8596 13.8615 16.9999 10.002 17C8.74212 17 7.56285 16.6676 6.54102 16.083L3.4668 16.7666L3.49121 16.6924L3.45996 16.7031L4.33301 14.1025C3.4939 12.9502 3 11.5312 3 10C3 6.13957 6.13851 3.00011 9.99805 3H10.002ZM7.36426 6.20117C7.09667 6.20126 6.8182 6.28088 6.64941 6.45312C6.4438 6.66312 5.93364 7.15184 5.93359 8.15625C5.93359 9.1607 6.66571 10.1319 6.76465 10.2676C6.8679 10.4032 8.19287 12.4935 10.25 13.3457C11.8578 14.0122 12.3355 13.9509 12.7012 13.873C13.2358 13.7575 13.9063 13.3616 14.0752 12.8848C14.2437 12.408 14.244 12.0017 14.1953 11.9141C14.1454 11.8274 14.0093 11.7781 13.8037 11.6748C13.598 11.5724 12.5987 11.0786 12.4092 11.0127C12.2237 10.9427 12.0472 10.9673 11.9072 11.165C11.7105 11.4404 11.5169 11.7198 11.3604 11.8887C11.237 12.0199 11.0351 12.0368 10.8662 11.9668C10.6394 11.8722 10.0057 11.6504 9.22461 10.9551C8.61911 10.4161 8.20787 9.74422 8.08887 9.54297C7.9702 9.33756 8.07743 9.21848 8.17188 9.10742C8.27503 8.97983 8.37242 8.88843 8.47559 8.76953C8.57884 8.65053 8.63652 8.58824 8.70215 8.44824C8.77204 8.31266 8.72271 8.17255 8.67285 8.06934C8.62313 7.96427 8.21264 6.96113 8.04395 6.55469C7.90848 6.23058 7.80488 6.21871 7.59961 6.20996C7.52961 6.20646 7.45088 6.20117 7.36426 6.20117Z"
      fill="#62574F"
    />
  </svg>
);

const EmailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      d="M15.6875 5C16.3984 5 17 5.57292 17 6.25C17 6.66667 16.7812 7.03125 16.4531 7.26562L10.5195 11.5104C10.1914 11.7448 9.78125 11.7448 9.45312 11.5104L3.51953 7.26562C3.19141 7.03125 3 6.66667 3 6.25C3 5.57292 3.57422 5 4.3125 5H15.6875ZM8.93359 12.1875C9.5625 12.6302 10.4102 12.6302 11.0391 12.1875L17 7.91667V13.3333C17 14.2708 16.207 15 15.25 15H4.75C3.76562 15 3 14.2708 3 13.3333V7.91667L8.93359 12.1875Z"
      fill="#62574F"
    />
  </svg>
);

// ── Photo field ───────────────────────────────────────────────────────────────

interface PhotoFieldProps {
  value?: File[];
  onChange: (files: File[]) => void;
}

const PhotoField = ({ value = [], onChange }: PhotoFieldProps) => {
  const galleryRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const urlsRef = useRef<Map<File, string>>(new Map());

  const getUrl = (file: File) => {
    if (!urlsRef.current.has(file)) urlsRef.current.set(file, URL.createObjectURL(file));
    return urlsRef.current.get(file) as string;
  };

  const addFiles = (list: FileList | null) => {
    const incoming = Array.from(list || []).filter(f => {
      const ext = f.name.split('.').pop()?.toLowerCase() || '';
      return ALLOWED_EXTENSIONS.includes(ext);
    });
    const next = [...value, ...incoming].slice(0, MAX_PHOTOS);
    onChange(next);
  };

  const removeFile = (index: number) => {
    const file = value[index];
    const url = urlsRef.current.get(file);
    if (url) {
      URL.revokeObjectURL(url);
      urlsRef.current.delete(file);
    }
    onChange(value.filter((_, i) => i !== index));
  };

  const limitReached = value.length >= MAX_PHOTOS;

  return (
    <>
      <PhotoActions>
        <PhotoButton type="button" disabled={limitReached} onClick={() => galleryRef.current?.click()}>
          <GalleryIcon /> Escolher na galeria
        </PhotoButton>
        <PhotoButton type="button" disabled={limitReached} onClick={() => cameraRef.current?.click()}>
          <GalleryIcon /> Tirar foto
        </PhotoButton>
      </PhotoActions>
      {value.length > 0 && (
        <PhotoThumbs>
          {value.map((file, idx) => (
            <PhotoThumb key={`${file.name}-${idx}`}>
              <img src={getUrl(file)} alt={file.name} />
              <PhotoThumbRemove type="button" aria-label={`Remover ${file.name}`} onClick={() => removeFile(idx)}>
                ×
              </PhotoThumbRemove>
            </PhotoThumb>
          ))}
        </PhotoThumbs>
      )}
      <HiddenFileInput
        ref={galleryRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          addFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <HiddenFileInput
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          addFiles(e.target.files);
          e.target.value = '';
        }}
      />
    </>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

export const ChamadoSemLogin = ({
  revealedSections,
  isSuccess,
  options,
  pageLabels,
  isSubmitting,
  unitLabel,
  lockedLabelOf,
  continueSection,
  handleSubmit,
  control,
  errors,
  watch,
  isVisible,
  isRequired,
  isLocked,
  labelOf,
  clearDependents,
  searchArea,
  loadMoreAreas,
  searchEquipamento,
  loadMoreEquipamentos,
}: ChamadoSemLoginProps) => {
  const { brandColor, pageTitle, supportText, logoPath, successTitle, successSupportText } = pageLabels;
  const showTerms = isVisible('name') || isVisible('email');
  const selectedUnit = watch('unit');
  const lastSection = revealedSections[revealedSections.length - 1];
  const isLastSectionRevealed = lastSection === 'situation';

  const renderSelectField = (
    fieldId: FieldId,
    fieldOptions?: Array<{ label: string; value: string | number; [key: string]: unknown }>,
    extra?: {
      onChangeExtra?: (value: unknown) => void;
      searchMode?: string;
      dataMode?: string;
      onSearch?: (term: string) => void;
      onScrollEnd?: () => void;
    },
  ) => {
    if (!isVisible(fieldId)) return null;
    if (isLocked(fieldId)) {
      const lockedDisplay = lockedLabelOf(fieldId) || watch(fieldId);
      return (
        <Field key={fieldId}>
          <FieldLabel>{labelOf(fieldId)}</FieldLabel>
          <LockedFieldText>{lockedDisplay || '-'}</LockedFieldText>
        </Field>
      );
    }

    const requiresUnit = fieldId !== 'unit';
    const disabled = requiresUnit && !selectedUnit;
    const noOptions = !disabled && (!fieldOptions || fieldOptions.length === 0) && requiresUnit;

    return (
      <Field key={fieldId}>
        <FieldLabel>
          {labelOf(fieldId)}
          {isRequired(fieldId) && (
            <>
              {' '}
              <RequiredMark aria-hidden>*</RequiredMark>
            </>
          )}
        </FieldLabel>
        <Controller
          name={fieldId as any}
          control={control}
          rules={isRequired(fieldId) ? { required: 'Preenchimento obrigatório.' } : {}}
          render={props => (
            <StyledSelect
              {...props}
              size="medium"
              placeholder={noOptions ? 'Não há itens para selecionar' : 'Selecionar'}
              options={fieldOptions || []}
              disabled={disabled || noOptions}
              error={!!errors[fieldId]}
              errormessage={(errors[fieldId] as any)?.message}
              onChange={(value: unknown) => {
                props.onChange(value);
                extra?.onChangeExtra?.(value);
              }}
              ref={null}
              searchMode={extra?.searchMode}
              dataMode={extra?.dataMode}
              onSearch={extra?.onSearch}
              onScrollEnd={extra?.onScrollEnd}
            />
          )}
        />
      </Field>
    );
  };

  // ── Contact step ──────────────────────────────────────────────────────────

  const renderContactStep = () => (
    <>
      <SectionTitle>
        <ContactIcon /> Dados de contato
      </SectionTitle>

      {isVisible('name') && (
        <Field>
          <FieldLabel>
            {labelOf('name')}
            {isRequired('name') && (
              <>
                {' '}
                <RequiredMark aria-hidden>*</RequiredMark>
              </>
            )}
          </FieldLabel>
          <Controller
            name="name"
            control={control}
            rules={isRequired('name') ? { required: 'Preenchimento obrigatório.' } : {}}
            render={props => (
              <Input
                {...props}
                size="medium"
                placeholder="Digite seu nome"
                error={!!errors.name}
                errormessage={(errors.name as any)?.message}
              />
            )}
          />
        </Field>
      )}

      {isVisible('phone') && (
        <Field>
          <FieldLabel>
            {labelOf('phone')}
            {isRequired('phone') && (
              <>
                {' '}
                <RequiredMark aria-hidden>*</RequiredMark>
              </>
            )}
          </FieldLabel>
          <Controller
            name="phone"
            control={control}
            rules={isRequired('phone') ? { required: 'Preenchimento obrigatório.' } : {}}
            render={props => (
              <MaskedInput
                {...props}
                mask="(99) 99999-9999"
                maskChar={null}
                size="medium"
                placeholder="(00) 00000-0000"
                error={!!errors.phone}
                errormessage={(errors.phone as any)?.message}
              />
            )}
          />
        </Field>
      )}

      {isVisible('email') && (
        <Field>
          <FieldLabel>
            {labelOf('email')}
            {isRequired('email') && (
              <>
                {' '}
                <RequiredMark aria-hidden>*</RequiredMark>
              </>
            )}
          </FieldLabel>
          <Controller
            name="email"
            control={control}
            rules={{
              ...(isRequired('email') ? { required: 'Preenchimento obrigatório.' } : {}),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Informe um e-mail válido.',
              },
            }}
            render={props => (
              <Input
                {...props}
                type="email"
                size="medium"
                placeholder="email@exemplo.com"
                error={!!errors.email}
                errormessage={(errors.email as any)?.message}
              />
            )}
          />
        </Field>
      )}

      {showTerms && (
        <Controller
          name="acceptTerms"
          control={control}
          rules={{ required: 'Você precisa aceitar os termos para continuar.' }}
          render={props => (
            <>
              <TermsRow>
                <TermsCheckbox
                  type="checkbox"
                  checked={!!props.value}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => props.onChange(e.target.checked)}
                />
                <TermsText>
                  Estou ciente de que meus dados serão utilizados para fins de registro desta manutenção.{' '}
                  <TermsLink brandColor={brandColor} href={URLS.termsOfUse} target="_blank" rel="noopener noreferrer">
                    Termos de Uso
                  </TermsLink>
                </TermsText>
              </TermsRow>
              {errors.acceptTerms && <ErrorText>{(errors.acceptTerms as any)?.message}</ErrorText>}
            </>
          )}
        />
      )}
    </>
  );

  // ── Location step ─────────────────────────────────────────────────────────

  const renderLocationStep = () => (
    <>
      <SectionTitle>
        <LocationIcon /> Local do ocorrido
      </SectionTitle>

      {/* Unit — sempre visível e travado */}
      <Field>
        <FieldLabel>
          {labelOf('unit')}
          <>
            {' '}
            <RequiredMark aria-hidden>*</RequiredMark>
          </>
        </FieldLabel>
        <LockedFieldText>{unitLabel || watch('unit') || '-'}</LockedFieldText>
      </Field>

      {renderSelectField('areaGroup', options.areaGroups, {
        onChangeExtra: () => clearDependents('areaGroup'),
      })}
      {renderSelectField('areaSubgroup', options.areaSubgroups, {
        onChangeExtra: () => clearDependents('areaSubgroup'),
      })}
      {renderSelectField('area', options.areas, {
        onChangeExtra: () => clearDependents('area'),
        searchMode: 'remote',
        dataMode: 'infinite',
        onSearch: searchArea,
        onScrollEnd: loadMoreAreas,
      })}
      {renderSelectField('equipment', options.equipments, {
        searchMode: 'remote',
        dataMode: 'infinite',
        onSearch: searchEquipamento,
        onScrollEnd: loadMoreEquipamentos,
      })}
    </>
  );

  // ── Situation step ────────────────────────────────────────────────────────

  const renderSituationStep = () => (
    <>
      <SectionTitle>
        <SituationIcon /> Situação encontrada
      </SectionTitle>

      {renderSelectField('serviceType', options.systems, {
        onChangeExtra: () => clearDependents('serviceType'),
      })}
      {renderSelectField('foundSituation', options.typical)}
      {renderSelectField('priority', options.priorities)}
      {renderSelectField('sector', options.setores)}

      {/* Details — sempre visível e obrigatório */}
      {isVisible('details') && (
        <Field>
          <FieldLabel>
            {labelOf('details')}
            <>
              {' '}
              <RequiredMark aria-hidden>*</RequiredMark>
            </>
          </FieldLabel>
          <Controller
            name="details"
            control={control}
            rules={{ required: 'Preenchimento obrigatório.' }}
            render={props => (
              <Textarea
                {...props}
                error={!!errors.details}
                errormessage={(errors.details as any)?.message}
                disabled={!selectedUnit}
                size="custom"
                width="100%"
                placeholder="Descreva o que aconteceu para que possamos te ajudar da melhor forma"
                customStyle={{ padding: '12px', resize: 'none', overflow: 'auto', height: '96px', width: '100%' }}
              />
            )}
          />
        </Field>
      )}

      {isVisible('photos') && (
        <Field>
          <FieldLabel>
            {labelOf('photos')}
            {isRequired('photos') && (
              <>
                {' '}
                <RequiredMark aria-hidden>*</RequiredMark>
              </>
            )}{' '}
            <FieldSubtitle>(É possível adicionar até {MAX_PHOTOS} fotos)</FieldSubtitle>
          </FieldLabel>
          <Controller
            name="images"
            control={control}
            rules={
              isRequired('photos')
                ? { validate: (v: File[]) => (v || []).length > 0 || 'Adicione ao menos uma foto.' }
                : {}
            }
            render={props => <PhotoField value={props.value} onChange={props.onChange} />}
          />
          {errors.images && <ErrorText>{(errors.images as any)?.message}</ErrorText>}
        </Field>
      )}

      <TrackingTitle>Acompanhe o andamento do chamado:</TrackingTitle>
      <Controller
        name="whatsappContact"
        control={control}
        render={props => (
          <CheckRow>
            <input
              type="checkbox"
              checked={!!props.value}
              onChange={(e: ChangeEvent<HTMLInputElement>) => props.onChange(e.target.checked)}
            />
            <WhatsAppIcon />
            Permitir contato via WhatsApp.
          </CheckRow>
        )}
      />
      <Controller
        name="receiveEmail"
        control={control}
        render={props => (
          <CheckRow>
            <input
              type="checkbox"
              checked={!!props.value}
              onChange={(e: ChangeEvent<HTMLInputElement>) => props.onChange(e.target.checked)}
            />
            <EmailIcon />
            Receber atualizações via e-mail.
          </CheckRow>
        )}
      />
    </>
  );

  // ── Success step ──────────────────────────────────────────────────────────

  const renderSuccessStep = () => (
    <SuccessContainer>
      <SuccessIconWrapper>
        <svg width="44" height="30" viewBox="0 0 48 34" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path
            d="M5 18.5L17.5 30L43 4.5"
            stroke="#00A889"
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </SuccessIconWrapper>
      <SuccessTitle>{successTitle}</SuccessTitle>
      <SuccessText>{successSupportText}</SuccessText>
    </SuccessContainer>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  const logo = (
    <LogoWrapper>
      {logoPath ? <LogoImage src={`${REACT_APP_URL_IMAGES}${logoPath}`} alt="Logo" /> : <LogoBlack />}
    </LogoWrapper>
  );

  return (
    <Page $isSuccess={isSuccess}>
      {isSuccess ? (
        <>
          {logo}
          <SuccessCard>{renderSuccessStep()}</SuccessCard>
        </>
      ) : (
        <FormCard>
          {logo}
          <PageTitle>{pageTitle}</PageTitle>
          <SupportText>{supportText}</SupportText>

          {revealedSections.includes('contact') && renderContactStep()}
          {revealedSections.includes('location') && renderLocationStep()}
          {revealedSections.includes('situation') && renderSituationStep()}

          <ActionButton
            brandColor={brandColor}
            disabled={isSubmitting}
            onClick={isLastSectionRevealed ? handleSubmit : () => continueSection(lastSection)}
            type="button"
          >
            {isLastSectionRevealed ? (isSubmitting ? 'Enviando...' : 'Enviar') : 'Continuar'}
          </ActionButton>
        </FormCard>
      )}

      <Footer>
        <span>Desenvolvido por</span>
        <LogoBlack />
      </Footer>
    </Page>
  );
};
